import type { NormalizedViewportTarget } from '../contracts/viewport';

/**
 * Um elemento pode casar com mais de um target (selectors diferentes
 * apontando pro mesmo nó). Por isso `targets` é um Set: o elemento é
 * observado uma única vez pelo IntersectionObserver, mas o estado é
 * mantido por target (via `ViewportStateStore`, chave `targetKey + elementId`).
 */
export type ViewportRegistryEntry = {
  readonly elementId: string;
  readonly element: Element;
  readonly targets: Set<NormalizedViewportTarget>;
};

export type ViewportRegisterResult =
  | {
      readonly ok: true;
      readonly entry: ViewportRegistryEntry;
      readonly isNewElement: boolean;
    }
  | { readonly ok: false; readonly reason: 'limit_reached' };

/**
 * Registry por instância de `ViewportTracker`.
 * Element IDs (`vp_el_N`) são sequenciais por instância — nunca module-level.
 */
export class ViewportTargetRegistry {
  private readonly elementIds = new WeakMap<Element, string>();

  private readonly entriesById = new Map<string, ViewportRegistryEntry>();

  private sequence = 0;

  private readonly maxObservedElements: number;

  constructor(maxObservedElements: number) {
    this.maxObservedElements = Math.max(1, maxObservedElements);
  }

  tryRegister(
    element: Element,
    target: NormalizedViewportTarget
  ): ViewportRegisterResult {
    const existingId = this.elementIds.get(element);

    if (existingId !== undefined) {
      const entry = this.entriesById.get(existingId);
      if (entry) {
        entry.targets.add(target);
        return { ok: true, entry, isNewElement: false };
      }
    }

    if (this.entriesById.size >= this.maxObservedElements) {
      return { ok: false, reason: 'limit_reached' };
    }

    this.sequence += 1;
    const elementId = `vp_el_${this.sequence}`;
    const entry: ViewportRegistryEntry = {
      elementId,
      element,
      targets: new Set([target]),
    };

    this.elementIds.set(element, elementId);
    this.entriesById.set(elementId, entry);

    return { ok: true, entry, isNewElement: true };
  }

  unregister(element: Element): void {
    const elementId = this.elementIds.get(element);
    if (elementId === undefined) return;

    this.elementIds.delete(element);
    this.entriesById.delete(elementId);
  }

  unregisterById(elementId: string): void {
    const entry = this.entriesById.get(elementId);
    if (entry) {
      this.elementIds.delete(entry.element);
    }
    this.entriesById.delete(elementId);
  }

  getByElement(element: Element): ViewportRegistryEntry | undefined {
    const elementId = this.elementIds.get(element);
    return elementId !== undefined
      ? this.entriesById.get(elementId)
      : undefined;
  }

  getById(elementId: string): ViewportRegistryEntry | undefined {
    return this.entriesById.get(elementId);
  }

  allObserved(): ViewportRegistryEntry[] {
    return Array.from(this.entriesById.values());
  }

  isObserved(element: Element): boolean {
    return this.elementIds.has(element);
  }

  count(): number {
    return this.entriesById.size;
  }

  isAtCapacity(): boolean {
    return this.entriesById.size >= this.maxObservedElements;
  }

  /** Libera as referências internas; o WeakMap se esvazia sozinho via GC. */
  disconnect(): void {
    this.entriesById.clear();
  }
}
